import AdapterDateFns from '@mui/lab/AdapterDateFns';
import LocalizationProvider from '@mui/lab/LocalizationProvider';
import { CssBaseline } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useRoutes, useNavigate } from 'react-router-dom';
import {
  getCookie,
  removeCookie,
  setLocalStorage,
  refreshSelectedSitesDetails,
  logoutFromApp,
  localStorageMethod,
  decryptEncryptedJWT
} from 'src/common/config/FunctionHelper';
import router from 'src/router';
import { getSubscriptionDetailsStripe } from 'src/common/Scraper';
import { useUserDetails } from './hooks/apiHooks/useUserDetails';
import { API } from 'src/common/config/API';
import './index.css';
import {
  setUserEmail,
  cleanupInitialState,
  setIsLoginScan,
  setIsLoadingUserDetails,
  stateUserDetails
} from './redux/counterSlice';
import { RootState } from './redux/store';
import ThemeProvider from './theme/ThemeProvider';
import {
  localSeoSignupSourceSqsp,
  localseoSignupSqspFlow,
  paths,
  planNameCookie,
  userRoles
} from 'src/common/config/TextMessage';
import { INTERCOM_APP_ID } from 'src/appConfig';
import {
  setStateStripeSubscription,
  userCleanupInitialState
} from './redux/userReducer';
import {
  setIndividualRefreshTable,
  setRefreshSiteId,
  setRefreshTable,
  setStripeLoader,
  setTermModal,
  siteAuditCleanupInitialState,
  setOpenSnackBar,
  setOrbitAISnackBar
} from './redux/siteAuditReducer';
import {
  cleanupKeywordInitialState,
  setKeywordTrackingDetails
} from './redux/keywordResearch';
import Intercom from 'react-intercom';
import { loginGTMTrackEvent } from './common/Analytics';
import { CommonSnackBar } from './common/CommonSnackBar';
import OrbitAISnackbar from './content/orbitAi/Components/OrbitAISnackbar';
import { cleanupLocalSeoInitialState } from './redux/localSeo';
import {
  hasLocalSeoCookie,
  removeLocalSeoAllStorage,
  removeLocalSeoCookie,
  setLocalSeoCookie,
  setUtmCookie
} from './common/config/FunctionHelperPartTwo';
import { useQueryClient } from '@tanstack/react-query';
import { setAiVisibilityRedisResponse } from './redux/dashboard';
import { ErrorBoundary } from './common/ErrorBoundary';
import { useIntercomUserData } from './hooks/useIntercomUserData';
import { loadAnalytics } from './loadAnalytics';
import UpgradeNewPlanModal from './components/SubScription/UpgradeNewPlanModal';
import { sseService } from './common/services/sseService';
// import PlanExpiredDialog from './components/SubScription/PlanExpiredPopUp';

const AppContent = () => {
  const dispatch = useDispatch();
  const content = useRoutes(router);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [showPricingModal, setShowPricingModal] = useState(false);
  const [isPricingModalLoading, setIsPricingModalLoading] = useState(false);
  const [isUpgradeClicked, setIsUpgradeClicked] = useState(false);
  let subscription = useSelector((state: RootState) => state.user.subscription);
  let userDetails: any = useSelector(
    (state: RootState) => state.counter.userDetails
  );
  let isLoadingUserDetails = useSelector(
    (state: RootState) => state.counter.isLoadingUserDetails
  );
  let isLoginScan = useSelector(
    (state: RootState) => state.counter.isCallLoginScan
  );
  let isLocalSEOAddonBuy = useSelector(
    (state: RootState) => state.counter.isLocalSEOAddonBuy
  );
  const siteId = useSelector((state: RootState) => state.siteAudit.siteId) ?? 0;
  let openSnackBar =
    useSelector((state: RootState) => state.siteAudit.openSnackBar) || false;
  let orbitAISnackBar =
    useSelector((state: RootState) => state.siteAudit.orbitAISnackBar);

  const keywordTrackingSection =
    useSelector(
      (state: RootState) => state.keywordResearch.keywordTrackingSection
    ) ?? 0;
  const websiteMonitoringDetails: any =
    useSelector(
      (state: RootState) => state.siteAudit.websiteMonitoringDetails
    ) ?? {};
  let activePlanName = useSelector((state: RootState) => state.user.activePlan);
  let planDetails = useSelector((state: RootState) => state.user.planDetails);
  let isUpgradedToSeo = useSelector(
    (state: RootState) => state.localSeo.isUpgradedToSeo
  );
  const isLocalSeoExtResultAccessed = useSelector(
    (state: RootState) => state.localSeo.isLocalSeoExtResultAccessed
  );
  const isAiAddonPurchased = useSelector(
    (state: RootState) => state.dashboard.isAiAddonPurchased
  );
  const isFreeUserWithAiAddon = userDetails?.isFreeUserWithAiAddon || false;
  const isAiVisibilityVisit = useSelector(
    (state: RootState) => state.dashboard.isAiVisibilityVisit
  );
  const isAiScoreView = useSelector(
    (state: RootState) => state.dashboard.isAiScoreView
  );
  const reAuditProcess = localStorageMethod('get', 'reaudit_process');
  const isReAuditProcess = reAuditProcess === 'true';
  const isScanInitialized = useRef(false);
  const webLocation = useLocation();
  const isLocalSeoSession = hasLocalSeoCookie();
  const isBillingPricePage = webLocation.pathname === paths?.billing || webLocation.pathname === paths?.googleBusiness;
  const isMemberUser = userDetails?.role_type === userRoles?.member;

  const isUpgradeSessionClicked = typeof window !== 'undefined' && (() => {
    try {
      return localStorage.getItem('hideUpgradePricingModal') === 'true';
    } catch (e) {
      return false;
    }
  })();

  const isShowStayOnCurrentPlanModal = !isMemberUser && !isUpgradeClicked && !isUpgradeSessionClicked && !isBillingPricePage && !isLoadingUserDetails && userDetails && userDetails?.email &&
    (userDetails?.pricingVersion === undefined ||
      userDetails?.pricingVersion === null);

  // Restrict Free user to access
  // const isExcludedPage =
  //   webLocation.pathname === paths?.billing ||
  //   webLocation.pathname === paths?.price ||
  //   webLocation.pathname === paths?.plan;

  // Restrict Free user to access
  // const showFreePlanModal = Boolean(
  //   userDetails?.email &&
  //     activePlanName &&
  //     activePlanName === planDetails?.free &&
  //     !isExcludedPage &&
  //     !isFreeUserWithAiAddon
  // );

  const getLocalSeoCookie = getCookie(localseoSignupSqspFlow) ?? null;
  const isLocalSeoSqSp = getLocalSeoCookie === localSeoSignupSourceSqsp;
  const clarityIdentified = useRef(false);

  const { userData, setUserData } = useIntercomUserData({
    userDetails,
    isLocalSEOAddonBuy,
    isUpgradedToSeo,
    isAiAddonPurchased,
    isAiVisibilityVisit,
    isAiScoreView,
    isLocalSeoSqSp,
    isLocalSeoExtResultAccessed
  });

  const [userId, setUserId] = useState<string | null>(null);

  const { data: userDetailsResponse, isError } = useUserDetails({
    enabled: !!userId && !userDetails?.email
  });

  useEffect(() => {
    const id = setTimeout(() => {
      loadAnalytics();
    }, 2000);

    return () => clearTimeout(id);
  }, []);

  useEffect(() => {
    if (userDetailsResponse) {
      const isLocalSeo = userDetailsResponse?.isLocalSeo || false;
      dispatch(setTermModal(userDetailsResponse?.has_agreed_to_terms));
      setUserData({
        name: userDetailsResponse?.name,
        email: userDetailsResponse?.email,
        is_plugin_installed: !!document.querySelector('#rough-water-seo-root'),
        user_hash: userDetailsResponse?.hash,
        localextension: isLocalSeo === true ? 'yes' : 'no'
      });
    } else if (isError) {
      // If the fetch completely fails
      logout();
    }
  }, [userDetailsResponse, isError, setUserData]);

  useEffect(() => {
    if (isShowStayOnCurrentPlanModal) {
      setShowPricingModal(true);
    }
  }, [isShowStayOnCurrentPlanModal]);

  const handleStayOnCurrentPlan = () => {
    setIsPricingModalLoading(true);
    API.updatePricingVersion(
      API.EndPoint.updatePricingVersion,
      API.Method.patch,
      undefined,
      { pricingVersion: 'v1' },
      (response: any) => {
        setIsPricingModalLoading(false);
        if (response?.data?.status === 'success') {
          setShowPricingModal(false);
          setIsUpgradeClicked(true);
        } else {
          dispatch(setOpenSnackBar({
            message: response?.data?.message || 'Something went wrong. Please try again later.',
            type: 'error'
          }));
        }
      }
    );
  };

  const handleUpgrade = () => {
    try {
      localStorage.setItem('hideUpgradePricingModal', 'true');
    } catch (e) { }
    setIsUpgradeClicked(true);
    setShowPricingModal(false);
    navigate(paths.billing);
  };

  const matchPath = [
    '/site-audit',
    '/settings',
    '/billing',
    '/external-scans',
    '/user-management',
    '/seo-tasks',
    '/keyword-research',
    '/lead-generation',
    '/keyword-research/keyword-explorer',
    '/keyword-research/competitor-analysis',
    '/keyword-research/blog-post-ideas',
    '/on-boarding',
    '/status/check',
    '/dashboard',
    '/keyword-research/keyword-tracking',
    '/other-tools',
    '/other-tools/backlinks',
    '/other-tools/local-seo-tools',
    '/keyword-research/keywords-lists',
    '/local-seo',
    '/backlinks',
    '/local-seo/business-profile',
    '/google-business',
    '/local-seo/map-rank-tracker',
    '/local-seo/tasks',
    '/local-seo/google-reviews',
    '/local-seo/business-posts',
    '/local-seo/listing-manager',
    '/ai-visibility'
  ];

  const refreshScoreArray = [
    paths?.siteAudit,
    paths?.scannedPages,
    paths?.dashboard
  ];

  // Cleanup function - call this on logout and when SSE should stop
  const cleanupSSE = () => {
    sseService.unsubscribe('app');
  };

  useEffect(() => {
    if (
      !clarityIdentified.current &&
      typeof window !== 'undefined' &&
      window.clarity &&
      userDetails?._id
    ) {
      // identify user
      window.clarity('identify', String(userDetails.email));

      // send email as custom property
      if (userDetails?.email) {
        window.clarity('set', 'email', userDetails.email);
      }

      clarityIdentified.current = true;
    }
  }, [userDetails]);

  useEffect(() => {
    const handleDocumentLoad = () => {
      const cookieData = getCookie(process.env.REACT_APP_COOKIE_NAME);
      if (cookieData && cookieData !== 'false') loginGTMTrackEvent(true);
    };
    window.onload = handleDocumentLoad;
    return () => {
      window.onload = null;
    };
  }, []);

  useEffect(() => {
    try {
      if (
        subscription?.subscription_id &&
        activePlanName !== planDetails?.localseo &&
        isLocalSeoSession &&
        !isBillingPricePage
      ) {
        removeLocalSeoCookie();
      } else if (
        subscription?.subscription_id &&
        activePlanName === planDetails?.localseo &&
        !isLocalSeoSession
      ) {
        setLocalSeoCookie();
      }
    } catch (error) {
      // Optional: report to error tracking service (e.g. Sentry/Bugsnag)
    }
  }, [subscription]);

  useEffect(() => {
    if (activePlanName) {
      setUtmCookie(planNameCookie, activePlanName);
    }
  }, [activePlanName]);

  const logout = async () => {
    await logoutFromApp();
    cleanupSSE();
    sseService.disconnectAll();
    removeLocalSeoAllStorage();
    try {
      localStorage.removeItem('hideUpgradePricingModal');
    } catch (e) { }
    dispatch(cleanupInitialState());
    dispatch(userCleanupInitialState());
    dispatch(siteAuditCleanupInitialState());
    dispatch(cleanupKeywordInitialState());
    dispatch(cleanupLocalSeoInitialState());
    queryClient.clear();
    if (window.clarity) {
      window.clarity('identify', null);
    }
  };

  const loginScan = () => {
    dispatch(setIsLoginScan(false));

    sseService.subscribe('app', {
      siteId,
      eventHandlers: {
        'site-update': (updates: any) => {
          if (!Array.isArray(updates)) return;
          const currentPath = window.location.pathname;

          updates.forEach((eventData) => {
            if (refreshScoreArray.includes(currentPath)) {
              refreshSelectedSitesDetails(eventData);
            }

            if (
              eventData?.sites_audit === true &&
              refreshScoreArray.includes(currentPath)
            ) {
              dispatch(setRefreshSiteId(eventData._id));
              dispatch(setRefreshTable(true));
            } else if (
              eventData?.sites_audit === false &&
              (currentPath === paths.externalScans ||
                refreshScoreArray.includes(currentPath))
            ) {
              dispatch(setIndividualRefreshTable(true));
              if (refreshScoreArray.includes(currentPath)) {
                refreshSelectedSitesDetails(eventData);
              }
            }
          });
        },
        'keyword-update': (updates: any) => {
          if (!Array.isArray(updates)) return;
          updates.forEach((eventData) => {
            dispatch(setKeywordTrackingDetails(eventData));
          });
        },
        'ai-visibility-update': (updates: any) => {
          if (!Array.isArray(updates)) return;
          updates.forEach((eventData: any) => {
            if (eventData?.siteId && eventData.siteId === siteId) {
              dispatch(setAiVisibilityRedisResponse(eventData));
            }
          });
        }
      }
    });
  };

  const getUserAndSubscriptionDetails = async () => {
    try {
      const encodedJWT = getCookie(process.env.REACT_APP_COOKIE_NAME);

      const jwtDecode: any = await decryptEncryptedJWT(encodedJWT);
      const fetchedUserId = jwtDecode.userId;

      if (fetchedUserId) {
        setUserId(fetchedUserId);
      }

      if (fetchedUserId && !subscription?.subscription_id) {
        dispatch(setStripeLoader(true));
        try {
          const subscriptionDetailsStripe =
            await getSubscriptionDetailsStripe();
          dispatch(setStateStripeSubscription(subscriptionDetailsStripe || {}));
        } catch (error) {
          console.error('Error fetching subscription details:', error);
        } finally {
          dispatch(setStripeLoader(false));
        }
      }
    } catch (e) {
      dispatch(setIsLoadingUserDetails(false));
      dispatch(setStripeLoader(false));
      if (getCookie(process.env.REACT_APP_COOKIE_NAME)) {
        logout();
      }
    }
  };

  // Remove invalid cookie
  if (
    !getCookie(process.env.REACT_APP_COOKIE_NAME) ||
    getCookie(process.env.REACT_APP_COOKIE_NAME) === 'false'
  ) {
    removeCookie();
  }

  useEffect(() => {
    (async () => {
      if (webLocation) {
        const isSuccess = webLocation.pathname.includes('success');
        const sessionId = new URLSearchParams(webLocation.search).get(
          'session_id'
        );

        if (!isSuccess && !sessionId) {
          getUserAndSubscriptionDetails();
        }

        if (matchPath.includes(webLocation.pathname)) {
          setTimeout(() => {
            const widget: any = document.querySelector('.intercom-launcher');
            if (widget) widget.style.visibility = 'inherit';
          }, 1000);
        } else {
          setTimeout(() => {
            const widget: any = document.querySelector('.intercom-launcher');
            if (widget) widget.style.visibility = 'hidden';
          }, 1000);
        }

        const cookieToken = getCookie(process.env.REACT_APP_COOKIE_NAME);
        if (cookieToken && cookieToken !== 'false') {
          const jwtDecode: any = await decryptEncryptedJWT(cookieToken);
          if (jwtDecode) {
            dispatch(setUserEmail(jwtDecode.email));
            setLocalStorage(cookieToken);
          }
        }
      }
    })();
  }, [webLocation]);

  useEffect(() => {
    const isKeywordProgress =
      (keywordTrackingSection === 3 ||
        websiteMonitoringDetails?.keywordProcessing?.isProcessing) &&
      webLocation.pathname === paths?.keywordTracking;

    if (matchPath.includes(webLocation.pathname) && isKeywordProgress) {
      loginScan();
    } else if (
      (matchPath.includes(webLocation.pathname) || isLoginScan) &&
      !isScanInitialized.current &&
      siteId
    ) {
      isScanInitialized.current = true;
      loginScan();
    }
  }, [
    siteId,
    webLocation.pathname,
    isLoginScan,
    keywordTrackingSection,
    websiteMonitoringDetails
  ]);

  // IMPORTANT: Add cleanup on component unmount
  useEffect(() => {
    return () => {
      cleanupSSE();
    };
  }, []);
  // OPTIONAL: If you want to disconnect when leaving certain pages
  useEffect(() => {
    // Disconnect SSE when leaving matchPath pages
    if (!matchPath.includes(webLocation.pathname)) {
      cleanupSSE();
    }
  }, [webLocation.pathname]);

  useEffect(() => {
    const currentPath = webLocation.pathname.startsWith('/')
      ? webLocation.pathname.substring(1)
      : webLocation.pathname;
    const relevantPaths = [
      paths?.dashboard?.substring(1),
      paths?.siteAudit?.substring(1),
      paths?.billing?.substring(1),
      paths?.userManagement?.substring(1),
      paths?.seoTasks?.substring(1),
      paths?.externalScans?.substring(1),
      paths?.price?.substring(1),
      paths?.plan?.substring(1),
      paths?.keywordResearch?.substring(1),
      paths?.leadGeneration?.substring(1)
    ];
    if (relevantPaths.includes(currentPath) && isReAuditProcess) {
      localStorageMethod('remove', 'reaudit_process');
    }
  }, [webLocation.pathname]);

  return (
    <>
      <CommonSnackBar open={openSnackBar} />
      <OrbitAISnackbar
        open={Boolean(orbitAISnackBar)}
        onClose={() => dispatch(setOrbitAISnackBar(null))}
        status={orbitAISnackBar?.status || 'error'}
        message={orbitAISnackBar?.message}
      />
      {content}
      {userDetails?.email && <Intercom appID={INTERCOM_APP_ID} {...userData} />}
      {/* Restrict Free user to access */}
      {/* <Dialog open={showFreePlanModal} disableEscapeKeyDown fullScreen>
        <PlanExpiredDialog isFreePlan={true} />
      </Dialog> */}
      {activePlanName && planDetails && showPricingModal && (
        <UpgradeNewPlanModal
          showPricingModal={showPricingModal}
          handleStayOnCurrentPlan={handleStayOnCurrentPlan}
          handleUpgrade={handleUpgrade}
          loading={isPricingModalLoading}
        />
      )}
    </>
  );
};

const App = () => {
  return (
    <>
      <ThemeProvider>
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <CssBaseline />
          <ErrorBoundary>
            <AppContent />
          </ErrorBoundary>
        </LocalizationProvider>
      </ThemeProvider>
    </>
  );
};

export default App;
